import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { isTokenExpired, isValidJWTFormat, extractApplicationIdFromToken } from '../../utils/token';
import axios from 'axios';

interface JwtPayload {
  applicationId?: string;
  exp: number;
  [key: string]: any;
}

interface LoginResponse {
  token: string;
  refreshToken: string;
  expiration: string;
}

interface ValidationResponse {
  token: string;
  refreshToken: string;
  expiration: string;
}

const EmailTokenValidator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Basic JWT validation function
  const isValidJWT = (token: string): boolean => {
    return isValidJWTFormat(token) && !isTokenExpired(token);
  };

  // Call backend API to validate email token
  const validateEmailToken = async (token: string): Promise<ValidationResponse> => {
    const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    
    const response = await axios.post(`${baseURL}/auth/validate-emailtoken`, {
      token: token
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.status !== 200) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.data;
  };

  useEffect(() => {
    const handleTokenValidation = async () => {
      try {
        // Step 1: Extract token from query string
        const token = searchParams.get('token');
        
        if (!token) {
          setError('No token provided in URL');
          navigate('/unauthorized?reason=missing-token');
          return;
        }

        // Step 2: Basic JWT validation
        if (!isValidJWT(token)) {
          setError('Invalid or expired JWT token');
          navigate('/unauthorized?reason=invalid-token');
          return;
        }

        // Step 3: Extract applicationId from token before API call
        const applicationId = extractApplicationIdFromToken(token);

        // Step 4: Call backend API to validate token
        const validationResponse = await validateEmailToken(token);

        // Step 5: Handle success response
        if (validationResponse && validationResponse.token && validationResponse.refreshToken) {
          // Save authentication tokens
          setToken(validationResponse.token, validationResponse.refreshToken);

          // Step 6: Redirect to ApplicationList with applicationId from original token
          if (applicationId) {
            navigate('/applications', { 
              state: { 
                openApplicationId: applicationId,
                fromEmailToken: true 
              } 
            });
          } else {
            navigate('/applications');
          }
        } else {
          setError('Invalid response from server');
          navigate('/unauthorized?reason=invalid-response');
        }

      } catch (error: any) {
        console.error('Token validation error:', error);
        
        let errorReason = 'validation-failed';
        let errorMessage = 'Failed to validate token';

        if (error.response) {
          // Backend returned an error
          if (error.response.status === 400) {
            errorReason = 'invalid-token';
            errorMessage = 'Invalid token provided';
          } else if (error.response.status === 401) {
            errorReason = 'unauthorized';
            errorMessage = 'Token validation failed';
          } else if (error.response.status === 500) {
            errorReason = 'server-error';
            errorMessage = 'Server error occurred';
          }
        } else if (error.request) {
          // Network error
          errorReason = 'network-error';
          errorMessage = 'Unable to connect to server';
        }

        setError(errorMessage);
        navigate(`/unauthorized?reason=${errorReason}&message=${encodeURIComponent(errorMessage)}`);
      } finally {
        setLoading(false);
      }
    };

    handleTokenValidation();
  }, [searchParams, navigate, setToken]);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="text-center shadow-lg" style={{ maxWidth: '400px' }}>
          <Card.Body className="p-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <Card.Title>Validating Token</Card.Title>
            <Card.Text className="text-muted">
              Please wait while we validate your access token...
            </Card.Text>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="text-center shadow-lg" style={{ maxWidth: '500px' }}>
          <Card.Body className="p-5">
            <Alert variant="danger">
              <Alert.Heading>Validation Failed</Alert.Heading>
              <p>{error}</p>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return null; // Component will redirect, so no UI needed on success
};

export default EmailTokenValidator;
